<template>
  <van-popup
    v-model:show="visible"
    position="center"
    round
    :closeable="!submitting"
    :close-on-click-overlay="!submitting"
    class="reservation-popup"
  >
    <div v-if="product" class="popup-content">
      <div class="popup-heading">
        <h2>Reserve</h2>
        <p>Get first access when it goes on sale</p>
      </div>

      <div class="product-summary">
        <img v-if="!imageFailed" :src="product.imageUrl" :alt="product.name" @error="imageFailed = true">
        <div v-else class="product-image-fallback" aria-hidden="true">
          <van-icon name="photo-o" size="26" />
        </div>
        <div class="product-copy">
          <strong>{{ product.name }}</strong>
          <span>{{ priceText }}</span>
          <em>Reservation open</em>
        </div>
      </div>

      <div class="phone-field">
        <label for="pc-event-preorder-phone">Phone number</label>
        <van-field
          id="pc-event-preorder-phone"
          :model-value="phone"
          type="tel"
          inputmode="tel"
          placeholder="Please enter phone number"
          @update:model-value="$emit('update:phone', String($event))"
        />
        <span>Used only for this reservation and related updates.</span>
      </div>

      <div class="coupon-summary" :class="{ unavailable: product.voucherAvailable === false }">
        <van-icon name="coupon-o" size="24" />
        <div>
          <strong>{{ couponTitle }}</strong>
          <span>{{ couponDescription }}</span>
        </div>
      </div>

      <p class="reservation-note">
        A reservation does not guarantee stock. Products are sold on a first-come, first-served basis.
      </p>

      <van-button
        block
        type="primary"
        class="confirm-button"
        :loading="submitting"
        loading-text="Reserving..."
        @click="$emit('confirm')"
      >
        Confirm Reservation
      </van-button>
      <button class="cancel-button" type="button" :disabled="submitting" @click="visible = false">
        Cancel
      </button>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import type { EventPreorderProduct } from '@/api/models/event-preorder'

const props = defineProps<{
  show: boolean
  product: EventPreorderProduct | null
  phone: string
  submitting: boolean
}>()

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void
  (event: 'update:phone', value: string): void
  (event: 'confirm'): void
}>()

const { $currencySymbol, $toThousandsFn } = useNuxtApp()
const imageFailed = ref(false)

watch(() => props.product?.id, () => {
  imageFailed.value = false
})

const visible = computed({
  get: () => props.show,
  set: (value: boolean) => emit('update:show', value)
})

const priceText = computed(() => {
  if (!props.product) return ''
  return `Est. ${$currencySymbol()} ${$toThousandsFn(props.product.estimatedPriceMin)}-${$toThousandsFn(props.product.estimatedPriceMax)}`
})

const couponTitle = computed(() => {
  if (props.product?.voucherAvailable === false) return 'Coupon sold out'
  return props.product?.voucherName || 'Coupon included'
})

const couponDescription = computed(() => {
  if (props.product?.voucherAvailable === false) {
    return 'You can still complete the reservation.'
  }
  if (props.product?.voucherAmount != null) {
    return `${$currencySymbol()} ${$toThousandsFn(props.product.voucherAmount)} off after a successful claim`
  }
  return 'Coupon availability is confirmed when you reserve.'
})
</script>

<style scoped lang="scss">
.reservation-popup {
  width: 1040px;
  max-width: calc(100vw - 64px);
  max-height: 88vh;
  overflow-y: auto;
  button { cursor: pointer; }
  button:disabled { cursor: not-allowed; }
  button:focus-visible { outline: 4px solid #c91d2b; outline-offset: 4px; }
}

.popup-content {
  padding: 45px 37.5px calc(37.5px + env(safe-area-inset-bottom));
}

.popup-heading {
  padding-right: 62.5px;

  h2 {
    margin: 0;
    color: #171717;
    font-size: 42.5px;
    line-height: 1.25;
  }

  p {
    margin: 7.5px 0 0;
    color: #767676;
    font-size: 28.75px;
  }
}

.product-summary {
  display: flex;
  margin-top: 35px;
  padding: 22.5px;
  background: #f7f7f7;
  border: 1.25px solid #ededed;
  border-radius: 10px;

  img,
  .product-image-fallback {
    width: 140px;
    height: 140px;
    flex: 0 0 140px;
    border-radius: 7.5px;
    background: #fff;
  }

  img {
    object-fit: cover;
  }

  .product-image-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
  }

  > img,
  > .product-image-fallback {
    margin-right: 25px;
  }
}

.product-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > * + * {
    margin-top: 6.25px;
  }

  strong {
    max-width: 100%;
    color: #202020;
    font-size: 33.75px;
    line-height: 1.3;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  span {
    color: #616161;
    font-size: 27.5px;
  }

  em {
    padding: 3.75px 12.5px;
    color: #c91d2b;
    font-size: 23.75px;
    font-style: normal;
    background: #fff0f1;
    border-radius: 5px;
  }
}

.phone-field {
  margin-top: 32.5px;

  label {
    display: block;
    margin-bottom: 12.5px;
    color: #333;
    font-size: 28.75px;
    font-weight: 600;
  }

  :deep(.van-field) {
    padding: 25px 22.5px;
    border: 1.25px solid #d8d8d8;
    border-radius: 7.5px;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #898989;
    font-size: 23.75px;
    line-height: 1.4;
  }
}

.coupon-summary {
  display: flex;
  align-items: center;
  margin-top: 27.5px;
  padding: 22.5px;
  color: #704d08;
  background: #fff7df;
  border: 1.25px solid #f0dc9f;
  border-radius: 7.5px;

  > .van-icon {
    flex: 0 0 auto;
    margin-right: 17.5px;
  }

  &.unavailable {
    color: #666;
    background: #f5f5f5;
    border-color: #e2e2e2;
  }

  div {
    min-width: 0;
    display: flex;
    flex-direction: column;

    > * + * {
      margin-top: 3.75px;
    }
  }

  strong {
    font-size: 27.5px;
    line-height: 1.3;
  }

  span {
    font-size: 23.75px;
    line-height: 1.4;
  }
}

.reservation-note {
  margin: 25px 0;
  color: #767676;
  font-size: 23.75px;
  line-height: 1.5;
}

.confirm-button {
  height: 97.5px;
  font-size: 31.25px;
  font-weight: 600;
  background: #d91f2e;
  border-color: #d91f2e;
  border-radius: 7.5px;
}

.cancel-button {
  width: 100%;
  height: 77.5px;
  margin-top: 10px;
  color: #666;
  font-size: 27.5px;
  background: transparent;
  border: 0;
}
</style>
